import { useState, useEffect, useRef } from 'react'
import { supabase, formatErrorMessage } from '../lib/supabase'
import { useToast } from '../hooks/useToast'
import Modal from './Modal'
import ChannelManagement from './ChannelManagement'
import SpaceInviteModal from './SpaceInviteModal'
import { Upload, X, Image } from 'lucide-react'

/**
 * Space Settings Modal
 * Allows space owners to customize their space:
 * - Change space icon/picture
 * - Edit space name and description
 * - Change banner image
 * - Toggle public/private
 * - View and regenerate invite code
 * - Transfer ownership to another member
 */
export default function SpaceSettingsModal({ isOpen, onClose, space, userId, onUpdateSpace, onSpaceDeleted }) {
  const toast = useToast()
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon_url: '',
    banner_url: '',
    is_public: false
  })
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('overview') // overview, appearance, privacy, admin
  const [showInviteModal, setShowInviteModal] = useState(false)

  // Banner upload state
  const bannerInputRef = useRef(null)
  const iconInputRef = useRef(null)
  const [uploadingBanner, setUploadingBanner] = useState(false)
  const [uploadingIcon, setUploadingIcon] = useState(false)
  const [bannerPreview, setBannerPreview] = useState(null)
  const [iconPreview, setIconPreview] = useState(null)

  // Transfer Ownership state
  const [showTransferModal, setShowTransferModal] = useState(false)
  const [transferTargetUser, setTransferTargetUser] = useState(null)
  const [spaceMembers, setSpaceMembers] = useState([])
  const [loadingMembers, setLoadingMembers] = useState(false)
  const [transferring, setTransferring] = useState(false)

  // Conflict Resolution Modal state
  const [showConflictModal, setShowConflictModal] = useState(false)
  const [conflictData, setConflictData] = useState(null) // { existingSpace, incomingTransfer }

  // Delete Space Modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')

  useEffect(() => {
    if (space) {
      setFormData({
        name: space.name || '',
        description: space.description || '',
        icon_url: space.icon_url || '',
        banner_url: space.banner_url || '',
        is_public: space.is_public || false
      })
      // Initialize previews
      setBannerPreview(space.banner_url || null)
      setIconPreview(space.icon_url || null)
    }
  }, [space])

  // Handle banner file upload
  const handleBannerUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file (PNG, JPG, GIF)')
      return
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Banner must be under 10MB')
      return
    }

    try {
      setUploadingBanner(true)

      // Create preview
      const previewUrl = URL.createObjectURL(file)
      setBannerPreview(previewUrl)

      // Upload to Supabase Storage
      const fileExt = file.name.split('.').pop()
      const fileName = `space-${space.id}-banner-${Date.now()}.${fileExt}`

      const { data, error: uploadError } = await supabase.storage
        .from('banners')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        })

      if (uploadError) {
        if (uploadError.message.includes('Bucket not found')) {
          toast.error('Banner storage not configured. Please contact administrator.')
        } else {
          throw uploadError
        }
        setBannerPreview(space?.banner_url || null)
        return
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('banners')
        .getPublicUrl(fileName)

      // Delete old banner if exists
      if (space.banner_url && space.banner_url.includes('supabase')) {
        const oldFileName = space.banner_url.split('/').pop()
        await supabase.storage.from('banners').remove([oldFileName])
      }

      // Update form data
      setFormData(prev => ({ ...prev, banner_url: publicUrl }))
      toast.success('Banner uploaded successfully!')

    } catch (err) {
      console.error('Error uploading banner:', err)
      toast.error('Failed to upload banner')
      setBannerPreview(space?.banner_url || null)
    } finally {
      setUploadingBanner(false)
    }
  }

  // Handle icon file upload
  const handleIconUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file (PNG, JPG, GIF)')
      return
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Icon must be under 5MB')
      return
    }

    try {
      setUploadingIcon(true)

      // Create preview
      const previewUrl = URL.createObjectURL(file)
      setIconPreview(previewUrl)

      // Upload to Supabase Storage
      const fileExt = file.name.split('.').pop()
      const fileName = `space-${space.id}-icon-${Date.now()}.${fileExt}`

      const { data, error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        })

      if (uploadError) {
        if (uploadError.message.includes('Bucket not found')) {
          toast.error('Storage not configured. Please contact administrator.')
        } else {
          throw uploadError
        }
        setIconPreview(space?.icon_url || null)
        return
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName)

      // Delete old icon if exists
      if (space.icon_url && space.icon_url.includes('supabase')) {
        const oldFileName = space.icon_url.split('/').pop()
        await supabase.storage.from('avatars').remove([oldFileName])
      }

      // Update form data
      setFormData(prev => ({ ...prev, icon_url: publicUrl }))
      toast.success('Icon uploaded successfully!')

    } catch (err) {
      console.error('Error uploading icon:', err)
      toast.error('Failed to upload icon')
      setIconPreview(space?.icon_url || null)
    } finally {
      setUploadingIcon(false)
    }
  }

  // Remove banner
  const handleRemoveBanner = async () => {
    try {
      setUploadingBanner(true)

      // Delete from storage if exists
      if (formData.banner_url && formData.banner_url.includes('supabase')) {
        const fileName = formData.banner_url.split('/').pop()
        await supabase.storage.from('banners').remove([fileName])
      }

      // Update form data
      setFormData(prev => ({ ...prev, banner_url: '' }))
      setBannerPreview(null)
      toast.success('Banner removed')

    } catch (err) {
      console.error('Error removing banner:', err)
      toast.error('Failed to remove banner')
    } finally {
      setUploadingBanner(false)
    }
  }

  // Remove icon
  const handleRemoveIcon = async () => {
    try {
      setUploadingIcon(true)

      // Delete from storage if exists
      if (formData.icon_url && formData.icon_url.includes('supabase')) {
        const fileName = formData.icon_url.split('/').pop()
        await supabase.storage.from('avatars').remove([fileName])
      }

      // Update form data
      setFormData(prev => ({ ...prev, icon_url: '' }))
      setIconPreview(null)
      toast.success('Icon removed')

    } catch (err) {
      console.error('Error removing icon:', err)
      toast.error('Failed to remove icon')
    } finally {
      setUploadingIcon(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      // Try to update via Supabase
      const { data, error } = await supabase
        .from('spaces')
        .update({
          name: formData.name,
          description: formData.description,
          icon_url: formData.icon_url,
          banner_url: formData.banner_url,
          is_public: formData.is_public,
          updated_at: new Date().toISOString()
        })
        .eq('id', space.id)
        .select()
        .single()

      if (error) throw error

      // Success
      onUpdateSpace(data || { ...space, ...formData })
      toast.success('Space updated successfully!')
      onClose()
    } catch (error) {
      console.error('Error updating space:', error)
      toast.error(formatErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }

  // Load space members for transfer ownership dropdown
  const loadSpaceMembers = async () => {
    if (!space) return

    setLoadingMembers(true)
    try {
      const { data, error } = await supabase
        .from('space_members')
        .select(`
          user_id,
          role,
          profiles:user_id (
            id,
            username,
            avatar_url
          )
        `)
        .eq('space_id', space.id)
        .neq('user_id', userId) // Exclude current owner

      if (error) throw error
      setSpaceMembers(data || [])
    } catch (error) {
      console.error('Error loading space members:', error)
      toast.error(formatErrorMessage(error))
    } finally {
      setLoadingMembers(false)
    }
  }

  // Check if target user already owns a space
  const checkTargetOwnership = async (targetUserId) => {
    try {
      const { data, error } = await supabase
        .from('spaces')
        .select('id, name')
        .eq('owner_id', targetUserId)
        .maybeSingle()

      if (error) throw error
      return data // Returns null if no space owned, or the space object
    } catch (error) {
      console.error('Error checking target ownership:', error)
      return null
    }
  }

  // Handle transfer ownership initiation
  const handleInitiateTransfer = async () => {
    if (!transferTargetUser) {
      toast.error('Please select a member to transfer ownership to')
      return
    }

    setTransferring(true)
    try {
      // Check if target user already owns a space
      const existingSpace = await checkTargetOwnership(transferTargetUser.user_id)

      if (existingSpace) {
        // Show conflict resolution modal
        setConflictData({
          existingSpace,
          targetUser: transferTargetUser.profiles,
          incomingSpace: space
        })
        setShowTransferModal(false)
        setShowConflictModal(true)
      } else {
        // No conflict, proceed with transfer
        await executeTransfer(transferTargetUser.user_id)
      }
    } catch (error) {
      console.error('Error initiating transfer:', error)
      toast.error(formatErrorMessage(error))
    } finally {
      setTransferring(false)
    }
  }

  // Execute the actual transfer
  const executeTransfer = async (newOwnerId) => {
    try {
      const { error } = await supabase
        .from('spaces')
        .update({
          owner_id: newOwnerId,
          updated_at: new Date().toISOString()
        })
        .eq('id', space.id)

      if (error) throw error

      toast.success('Ownership transferred successfully!')
      setShowTransferModal(false)
      setShowConflictModal(false)
      setTransferTargetUser(null)
      onClose()
    } catch (error) {
      console.error('Error transferring ownership:', error)
      toast.error(formatErrorMessage(error))
    }
  }

  // Handle conflict resolution: Delete existing space and accept transfer
  const handleDeleteAndAccept = async () => {
    if (!conflictData) return

    setTransferring(true)
    try {
      // Delete the target user's existing space
      const { error: deleteError } = await supabase
        .from('spaces')
        .delete()
        .eq('id', conflictData.existingSpace.id)

      if (deleteError) throw deleteError

      // Now execute the transfer
      await executeTransfer(conflictData.targetUser.id)

      toast.success(`Space "${conflictData.existingSpace.name}" deleted and ownership transferred!`)
    } catch (error) {
      console.error('Error in delete and accept:', error)
      toast.error(formatErrorMessage(error))
    } finally {
      setTransferring(false)
      setConflictData(null)
    }
  }

  // Handle conflict resolution: Decline the transfer
  const handleDeclineTransfer = () => {
    setShowConflictModal(false)
    setConflictData(null)
    toast.info('Transfer declined')
  }

  // Handle space deletion
  const handleDeleteSpace = async () => {
    setDeleting(true)
    try {
      const { error } = await supabase
        .from('spaces')
        .delete()
        .eq('id', space.id)

      if (error) throw error

      toast.success('Space deleted successfully')
      setShowDeleteModal(false)
      setDeleteConfirmText('')
      onSpaceDeleted?.(space.id)
      onClose()
    } catch (error) {
      console.error('Error deleting space:', error)
      toast.error(formatErrorMessage(error))
    } finally {
      setDeleting(false)
    }
  }

  if (!isOpen || !space) return null

  // Check if user is the owner
  const isOwner = space.owner_id === userId

  if (!isOwner) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Space Settings" size="small">
        <div className="text-center py-8">
          <div className="text-4xl mb-3">🔒</div>
          <p className="text-slate-400">Only the space owner can edit these settings</p>
        </div>
      </Modal>
    )
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`${space.name} Settings`} size="large" noPadding>
      <div className="flex h-full min-h-[500px]">
        {/* Vertical Sidebar Navigation */}
        <div className="w-48 flex-shrink-0 border-r border-white/10 bg-black/20 backdrop-blur-sm rounded-l-lg">
          <nav className="p-3 space-y-1">
            <button
              type="button"
              onClick={() => setActiveTab('overview')}
              className={`w-full text-left px-3 py-2.5 rounded-lg transition-all flex items-center gap-3 ${
                activeTab === 'overview'
                  ? 'bg-gjyl-indigo-500/30 text-white border border-gjyl-indigo-500/50'
                  : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <span className="text-base">📋</span>
              <span className="text-sm font-medium">Overview</span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('channels')}
              className={`w-full text-left px-3 py-2.5 rounded-lg transition-all flex items-center gap-3 ${
                activeTab === 'channels'
                  ? 'bg-gjyl-indigo-500/30 text-white border border-gjyl-indigo-500/50'
                  : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <span className="text-base">📺</span>
              <span className="text-sm font-medium">Channels</span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('appearance')}
              className={`w-full text-left px-3 py-2.5 rounded-lg transition-all flex items-center gap-3 ${
                activeTab === 'appearance'
                  ? 'bg-gjyl-indigo-500/30 text-white border border-gjyl-indigo-500/50'
                  : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <span className="text-base">🎨</span>
              <span className="text-sm font-medium">Appearance</span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('privacy')}
              className={`w-full text-left px-3 py-2.5 rounded-lg transition-all flex items-center gap-3 ${
                activeTab === 'privacy'
                  ? 'bg-gjyl-indigo-500/30 text-white border border-gjyl-indigo-500/50'
                  : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <span className="text-base">📨</span>
              <span className="text-sm font-medium">Privacy</span>
            </button>

            {/* Separator before Admin section */}
            <div className="border-t border-white/10 my-2 pt-2">
              <span className="px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Admin</span>
            </div>

            <button
              type="button"
              onClick={() => {
                setActiveTab('admin')
                loadSpaceMembers()
              }}
              className={`w-full text-left px-3 py-2.5 rounded-lg transition-all flex items-center gap-3 ${
                activeTab === 'admin'
                  ? 'bg-gjyl-indigo-500/30 text-white border border-gjyl-indigo-500/50'
                  : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <span className="text-base">⚙️</span>
              <span className="text-sm font-medium">Settings</span>
            </button>
          </nav>
        </div>

        {/* Main Content Area */}
        <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto scrollbar-gjyl p-6">
            {/* Channels Tab */}
            {activeTab === 'channels' && (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-white mb-4">Channels</h2>
                <ChannelManagement
                  space={space}
                  onChannelCreated={(channel) => {
                    console.log('Channel created:', channel)
                    // Optionally notify parent component
                  }}
                />
              </div>
            )}

            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-white mb-4">Overview</h2>
                <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Space Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-gjyl"
                  placeholder="My Awesome Space"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="input-gjyl resize-none"
                  placeholder="What's this space about?"
                  rows={3}
                />
              </div>

              <div className="glass rounded-gjyl p-4">
                <div className="flex items-center gap-3 mb-2">
                  <svg className="w-5 h-5 text-gjyl-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm font-medium text-white">Space Information</span>
                </div>
                <div className="space-y-2 text-sm text-slate-400">
                  <div className="flex justify-between">
                    <span>Created:</span>
                    <span className="text-white">{new Date(space.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Space ID:</span>
                    <span className="text-white font-mono text-xs">{space.id}</span>
                  </div>
                </div>
                </div>
              </div>
            )}

            {/* Appearance Tab */}
            {activeTab === 'appearance' && (
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-white mb-4">Appearance</h2>
                {/* Space Icon */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Space Icon
                </label>
                <div className="flex items-center gap-4 mb-3">
                  <div className="relative group">
                    <div className="w-20 h-20 rounded-2xl bg-gjyl-slate-700 flex items-center justify-center font-bold text-2xl overflow-hidden border-2 border-gjyl-slate-600">
                      {iconPreview || formData.icon_url ? (
                        <img src={iconPreview || formData.icon_url} alt="Space icon" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-slate-400">{formData.name.substring(0, 2).toUpperCase()}</span>
                      )}
                    </div>
                    {/* Upload overlay */}
                    <div
                      onClick={() => iconInputRef.current?.click()}
                      className="absolute inset-0 bg-black/60 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer flex items-center justify-center"
                    >
                      {uploadingIcon ? (
                        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-white"></div>
                      ) : (
                        <Upload className="w-6 h-6 text-white" />
                      )}
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-slate-400 mb-2">
                      Click to upload an icon image (PNG, JPG, GIF - max 5MB)
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => iconInputRef.current?.click()}
                        disabled={uploadingIcon}
                        className="px-3 py-1.5 glass hover:glass-strong rounded-gjyl text-sm text-white transition-all flex items-center gap-2"
                      >
                        <Upload className="w-4 h-4" />
                        Upload Icon
                      </button>
                      {(iconPreview || formData.icon_url) && (
                        <button
                          type="button"
                          onClick={handleRemoveIcon}
                          disabled={uploadingIcon}
                          className="px-3 py-1.5 glass hover:bg-gjyl-danger/20 rounded-gjyl text-sm text-gjyl-danger transition-all flex items-center gap-2"
                        >
                          <X className="w-4 h-4" />
                          Remove
                        </button>
                      )}
                    </div>
                    <input
                      ref={iconInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleIconUpload}
                      className="hidden"
                    />
                  </div>
                </div>
              </div>

              {/* Space Banner */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Space Banner (Optional)
                </label>
                <div className="relative group mb-3">
                  <div className={`w-full h-32 rounded-gjyl overflow-hidden border-2 border-gjyl-slate-600 ${!bannerPreview && !formData.banner_url ? 'bg-gradient-to-r from-gjyl-indigo-500/30 to-gjyl-slate-700' : ''}`}>
                    {(bannerPreview || formData.banner_url) ? (
                      <img src={bannerPreview || formData.banner_url} alt="Space banner" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="text-center">
                          <Image className="w-8 h-8 text-slate-500 mx-auto mb-2" />
                          <span className="text-sm text-slate-500">No banner set</span>
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Upload overlay */}
                  <div
                    onClick={() => bannerInputRef.current?.click()}
                    className="absolute inset-0 bg-black/60 rounded-gjyl opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer flex items-center justify-center"
                  >
                    {uploadingBanner ? (
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-white"></div>
                    ) : (
                      <div className="text-center">
                        <Upload className="w-8 h-8 text-white mx-auto mb-2" />
                        <span className="text-sm text-white">Click to upload banner</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 mb-2">
                  <button
                    type="button"
                    onClick={() => bannerInputRef.current?.click()}
                    disabled={uploadingBanner}
                    className="px-3 py-1.5 glass hover:glass-strong rounded-gjyl text-sm text-white transition-all flex items-center gap-2"
                  >
                    <Upload className="w-4 h-4" />
                    Upload Banner
                  </button>
                  {(bannerPreview || formData.banner_url) && (
                    <button
                      type="button"
                      onClick={handleRemoveBanner}
                      disabled={uploadingBanner}
                      className="px-3 py-1.5 glass hover:bg-gjyl-danger/20 rounded-gjyl text-sm text-gjyl-danger transition-all flex items-center gap-2"
                    >
                      <X className="w-4 h-4" />
                      Remove Banner
                    </button>
                  )}
                </div>
                <input
                  ref={bannerInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleBannerUpload}
                  className="hidden"
                />
                <p className="text-xs text-slate-400">
                  Recommended size: 1200x400px (PNG, JPG, GIF - max 10MB)
                </p>
              </div>

              {/* Color Theme (Future feature placeholder) */}
              <div className="glass rounded-gjyl p-4 opacity-50">
                <div className="flex items-center gap-2 mb-2">
                  <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                  </svg>
                  <span className="text-sm font-medium text-slate-300">Custom Theme Color</span>
                  <span className="text-xs bg-gjyl-slate-700 px-2 py-0.5 rounded text-slate-400">Coming Soon</span>
                </div>
                <p className="text-xs text-slate-500">
                  Choose a custom accent color for your space
                </p>
              </div>
              </div>
            )}

            {/* Privacy & Invites Tab */}
            {activeTab === 'privacy' && (
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-white mb-4">Privacy & Invites</h2>
                {/* Public/Private Toggle */}
              <div>
                <label className="flex items-center justify-between p-4 glass rounded-gjyl cursor-pointer hover:glass-strong transition-all">
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-gjyl-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <div className="text-sm font-medium text-white">Public Space</div>
                      <div className="text-xs text-slate-400">Anyone can discover and join this space</div>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={formData.is_public}
                    onChange={(e) => setFormData({ ...formData, is_public: e.target.checked })}
                    className="w-5 h-5 rounded border-gjyl-slate-600 text-gjyl-indigo-500 focus:ring-gjyl-indigo-500 focus:ring-offset-0"
                  />
                </label>
              </div>

              {/* Enhanced Invite System */}
              <div className="glass rounded-gjyl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-5 h-5 text-gjyl-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                  <span className="text-sm font-medium text-white">Invite Members</span>
                </div>

                <p className="text-xs text-slate-400 mb-4">
                  Invite friends to your space with customizable invite links, expiration settings, and usage limits.
                </p>

                <button
                  type="button"
                  onClick={() => setShowInviteModal(true)}
                  className="w-full px-4 py-3 bg-gjyl-indigo-500 hover:bg-gjyl-indigo-400 rounded-gjyl text-white font-medium transition-all hover:scale-105 flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span>Manage Invites</span>
                </button>

                <div className="mt-4 pt-4 border-t border-gjyl-slate-700">
                  <div className="text-xs text-slate-500 space-y-1">
                    <div className="flex items-center gap-2">
                      <svg className="w-3 h-3 text-gjyl-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Share custom invite links</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg className="w-3 h-3 text-gjyl-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Set expiration & usage limits</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg className="w-3 h-3 text-gjyl-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Invite friends directly</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg className="w-3 h-3 text-gjyl-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>View space members</span>
                    </div>
                  </div>
                </div>
                </div>
              </div>
            )}

            {/* Admin Tab */}
            {activeTab === 'admin' && (
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-white mb-4">Admin Settings</h2>
                {/* Transfer Ownership Section */}
              <div className="glass rounded-gjyl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-5 h-5 text-gjyl-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                  <span className="text-sm font-medium text-white">Transfer Ownership</span>
                </div>

                <p className="text-xs text-slate-400 mb-4">
                  Transfer ownership of this space to another member. This action cannot be undone.
                </p>

                <button
                  type="button"
                  onClick={() => setShowTransferModal(true)}
                  className="w-full px-4 py-3 glass hover:glass-strong rounded-gjyl text-gjyl-warning font-medium transition-all flex items-center justify-center gap-2 border border-gjyl-warning/30"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                  <span>Transfer Ownership</span>
                </button>
              </div>

              {/* Danger Zone */}
              <div className="glass rounded-gjyl p-5 border border-gjyl-danger/30">
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-5 h-5 text-gjyl-danger" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span className="text-sm font-medium text-gjyl-danger">Danger Zone</span>
                </div>

                <p className="text-xs text-slate-400 mb-4">
                  Deleting this space is permanent. All channels, messages, and members will be removed.
                </p>

                <button
                  type="button"
                  onClick={() => setShowDeleteModal(true)}
                  className="w-full px-4 py-3 bg-gjyl-danger/20 hover:bg-gjyl-danger/30 rounded-gjyl text-gjyl-danger font-medium transition-all flex items-center justify-center gap-2 border border-gjyl-danger/30"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  <span>Delete Space</span>
                </button>
                </div>
              </div>
            )}
          </div>

          {/* Submit Button - Fixed at bottom */}
          <div className="flex-shrink-0 flex gap-3 p-4 border-t border-white/10 bg-black/20">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 glass hover:glass-strong rounded-gjyl text-white transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2.5 bg-gjyl-indigo-500 hover:bg-gjyl-indigo-400 rounded-gjyl text-white font-medium transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>

      {/* Enhanced Invite Modal */}
      <SpaceInviteModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        space={space}
        userId={userId}
      />

      {/* Transfer Ownership Modal */}
      {showTransferModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="bg-gjyl-slate-900 border border-gjyl-slate-700 rounded-2xl shadow-gjyl-lift-strong w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-gjyl-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
              Transfer Ownership
            </h3>

            <p className="text-sm text-slate-400 mb-4">
              Select a member to transfer ownership of <strong className="text-white">{space.name}</strong> to.
              You will lose all owner privileges.
            </p>

            {loadingMembers ? (
              <div className="py-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gjyl-indigo-500 mx-auto"></div>
                <p className="text-slate-400 text-sm mt-2">Loading members...</p>
              </div>
            ) : spaceMembers.length === 0 ? (
              <div className="py-8 text-center">
                <p className="text-slate-400 text-sm">No other members in this space</p>
                <p className="text-slate-500 text-xs mt-1">Invite members first before transferring ownership</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto scrollbar-gjyl mb-4">
                {spaceMembers.map((member) => (
                  <button
                    key={member.user_id}
                    type="button"
                    onClick={() => setTransferTargetUser(member)}
                    className={`w-full flex items-center gap-3 p-3 rounded-gjyl transition-all ${
                      transferTargetUser?.user_id === member.user_id
                        ? 'bg-gjyl-indigo-500/30 border border-gjyl-indigo-500'
                        : 'glass hover:glass-strong'
                    }`}
                  >
                    <img
                      src={member.profiles?.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${member.user_id}`}
                      alt={member.profiles?.username}
                      className="w-10 h-10 rounded-full"
                    />
                    <div className="flex-1 text-left">
                      <div className="text-sm font-medium text-white">{member.profiles?.username || 'Unknown'}</div>
                      <div className="text-xs text-slate-400 capitalize">{member.role}</div>
                    </div>
                    {transferTargetUser?.user_id === member.user_id && (
                      <svg className="w-5 h-5 text-gjyl-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            )}

            <div className="flex gap-3 mt-4">
              <button
                type="button"
                onClick={() => {
                  setShowTransferModal(false)
                  setTransferTargetUser(null)
                }}
                className="flex-1 px-4 py-2.5 glass hover:glass-strong rounded-gjyl text-white transition-all"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleInitiateTransfer}
                disabled={!transferTargetUser || transferring}
                className="flex-1 px-4 py-2.5 bg-gjyl-warning hover:bg-gjyl-warning/80 rounded-gjyl text-black font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {transferring ? 'Transferring...' : 'Transfer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ownership Conflict Resolution Modal */}
      {showConflictModal && conflictData && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="bg-gjyl-slate-900 border border-gjyl-danger/50 rounded-2xl shadow-gjyl-lift-strong w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gjyl-danger mb-4 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              Ownership Conflict
            </h3>

            <div className="glass rounded-gjyl p-4 mb-4">
              <p className="text-sm text-slate-300 mb-3">
                <strong className="text-white">{conflictData.targetUser?.username}</strong> already owns a space:
              </p>
              <div className="flex items-center gap-3 p-3 bg-gjyl-slate-800 rounded-gjyl">
                <div className="w-10 h-10 rounded-gjyl bg-gjyl-slate-700 flex items-center justify-center text-sm font-bold text-slate-400">
                  {conflictData.existingSpace.name.substring(0, 2).toUpperCase()}
                </div>
                <div>
                  <div className="text-sm font-medium text-white">{conflictData.existingSpace.name}</div>
                  <div className="text-xs text-slate-400">Their current space</div>
                </div>
              </div>
            </div>

            <p className="text-sm text-slate-400 mb-6">
              Users can only own one space. To accept ownership of <strong className="text-white">{space.name}</strong>,
              they must first delete their existing space.
            </p>

            <div className="flex flex-col gap-3">
              <button
                type="button"
                onClick={handleDeclineTransfer}
                className="w-full px-4 py-3 glass hover:glass-strong rounded-gjyl text-white font-medium transition-all"
              >
                Decline Transfer
              </button>
              <button
                type="button"
                onClick={handleDeleteAndAccept}
                disabled={transferring}
                className="w-full px-4 py-3 bg-gjyl-danger hover:bg-gjyl-danger/80 rounded-gjyl text-white font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {transferring ? 'Processing...' : `Delete "${conflictData.existingSpace.name}" & Accept`}
              </button>
            </div>

            <p className="text-xs text-slate-500 text-center mt-4">
              Warning: Deleting a space is permanent and cannot be undone.
            </p>
          </div>
        </div>
      )}

      {/* Delete Space Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="bg-gjyl-slate-900 border border-gjyl-danger/50 rounded-2xl shadow-gjyl-lift-strong w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gjyl-danger mb-4 flex items-center gap-2">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete Space
            </h3>

            <div className="glass rounded-gjyl p-4 mb-4">
              <p className="text-sm text-slate-300 mb-2">
                You are about to permanently delete:
              </p>
              <div className="flex items-center gap-3 p-3 bg-gjyl-slate-800 rounded-gjyl">
                <div className="w-12 h-12 rounded-gjyl bg-gjyl-danger/20 flex items-center justify-center text-lg font-bold text-gjyl-danger">
                  {space.icon_url ? (
                    <img src={space.icon_url} alt={space.name} className="w-full h-full object-cover rounded-gjyl" />
                  ) : (
                    space.name.substring(0, 2).toUpperCase()
                  )}
                </div>
                <div>
                  <div className="text-base font-semibold text-white">{space.name}</div>
                  <div className="text-xs text-slate-400">All channels, messages & members will be removed</div>
                </div>
              </div>
            </div>

            <div className="bg-gjyl-danger/10 border border-gjyl-danger/30 rounded-gjyl p-4 mb-6">
              <p className="text-sm text-gjyl-danger font-medium mb-1">
                This action cannot be undone!
              </p>
              <p className="text-xs text-slate-400">
                Type <span className="font-mono text-gjyl-danger">{space.name}</span> to confirm deletion.
              </p>
            </div>

            <div className="mb-6">
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={`Type "${space.name}" to confirm`}
                className="w-full px-4 py-3 bg-gjyl-slate-800 border border-gjyl-slate-700 rounded-gjyl text-white placeholder-slate-500 focus:outline-none focus:border-gjyl-danger/50 transition-colors"
                autoFocus
              />
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteModal(false)
                  setDeleteConfirmText('')
                }}
                className="flex-1 px-4 py-3 glass hover:glass-strong rounded-gjyl text-white font-medium transition-all"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteSpace}
                disabled={deleteConfirmText !== space.name || deleting}
                className="flex-1 px-4 py-3 bg-gjyl-danger hover:bg-gjyl-danger/80 rounded-gjyl text-white font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {deleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete Forever
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  )
}
